import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { labAPI } from '../../services/api';
import { useNotify } from '../../context/NotifyContext';
import LabDemographicsSheet from './LabDemographicsSheet';

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const LabExtendedFeatures = ({ activeTab, clinics = [] }) => {
  const { showSuccess, showError } = useNotify();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [labStatusDraft, setLabStatusDraft] = useState({});
  const [labNoteDraft, setLabNoteDraft] = useState({});
  const [team, setTeam] = useState({ employees: [], phlebotomists: [] });
  const [reqFilterCategory, setReqFilterCategory] = useState('');
  const [expandedReqId, setExpandedReqId] = useState(null);

  const loadServiceRequests = useCallback(async () => {
    try {
      const { data } = await labAPI.getServiceRequests();
      setServiceRequests(data.requests || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await labAPI.getAuditLogs({ limit: 200 });
      setAuditLogs(data.logs || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await labAPI.getLabTeam();
      setTeam(data || { employees: [], phlebotomists: [] });
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'service-requests') {
      loadServiceRequests();
      loadTeam();
    }
  }, [activeTab, loadServiceRequests, loadTeam]);

  useEffect(() => {
    if (activeTab === 'audit-logs') loadAudit();
  }, [activeTab, loadAudit]);

  const applyLabStatus = async (id) => {
    const status = labStatusDraft[id];
    const payload = {};
    if (status) payload.status = status;
    if (Object.prototype.hasOwnProperty.call(labNoteDraft, id)) payload.labNote = labNoteDraft[id];
    if (!payload.status && !Object.prototype.hasOwnProperty.call(labNoteDraft, id)) return;
    setBusy(true);
    try {
      await labAPI.updateServiceRequest(id, payload);
      setLabStatusDraft((p) => ({ ...p, [id]: '' }));
      setLabNoteDraft((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
      await loadServiceRequests();
      showSuccess('Request updated.');
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (id, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    setBusy(true);
    try {
      await labAPI.uploadServiceRequestFiles(id, fd);
      await loadServiceRequests();
      showSuccess('Files attached.');
    } catch (err) {
      showError(err.response?.data?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadAtt = async (reqId, att) => {
    try {
      const res = await labAPI.downloadServiceAttachment(reqId, att._id);
      triggerBlobDownload(res.data, att.filename || 'attachment');
    } catch (e) {
      showError('Download failed');
    }
  };

  const saveAssignUser = async (reqId, userId) => {
    setBusy(true);
    try {
      await labAPI.updateServiceRequest(reqId, { assignedUser: userId || null });
      await loadServiceRequests();
      showSuccess('Assignee updated.');
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const assigneeOptions = useMemo(() => {
    const em = (team.employees || []).map((u) => ({
      id: u._id,
      label: `${u.name} (staff)`
    }));
    const ph = (team.phlebotomists || []).map((u) => ({
      id: u._id,
      label: `${u.name} (phlebotomist)`
    }));
    return [...em, ...ph];
  }, [team]);

  const filteredServiceRequests = useMemo(() => {
    if (!reqFilterCategory) return serviceRequests;
    return serviceRequests.filter((r) => (r.category || 'general') === reqFilterCategory);
  }, [serviceRequests, reqFilterCategory]);

  if (activeTab === 'service-requests') {
    return (
      <div className="card">
        <h2 className="card-title" style={{ marginBottom: '16px' }}>Clinic service requests</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Assign staff or a phlebotomist, move the request through statuses, and attach files. Expand a row for full clinic details.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <select className="form-input" style={{ minWidth: '200px' }} value={reqFilterCategory} onChange={(e) => setReqFilterCategory(e.target.value)}>
            <option value="">All types</option>
            <option value="general">General</option>
            <option value="phlebotomy">Phlebotomy / pickup</option>
            <option value="results">Results / reports</option>
            <option value="courier">Courier / logistics</option>
            <option value="supplies">Supplies</option>
            <option value="billing">Invoices & fees</option>
            <option value="other">Other</option>
          </select>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{filteredServiceRequests.length} request(s)</span>
        </div>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '36px' }} />
                <th>Title / type</th>
                <th>Clinic</th>
                <th>Assignee</th>
                <th>Status</th>
                <th>Lab action</th>
                <th>Files</th>
              </tr>
            </thead>
            <tbody>
              {filteredServiceRequests.map((r) => (
                <React.Fragment key={r._id}>
                  <tr>
                    <td>
                      <button type="button" className="btn btn-outline" style={{ padding: '2px 8px' }} onClick={() => setExpandedReqId((x) => (x === r._id ? null : r._id))}>
                        {expandedReqId === r._id ? '−' : '+'}
                      </button>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.title}</div>
                      <span className="badge badge-info" style={{ fontSize: '0.7rem', marginTop: '6px', display: 'inline-block' }}>
                        {r.category || 'general'}
                      </span>
                      {r.preferredAssigneeNote && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '6px' }}>Clinic note: {r.preferredAssigneeNote}</div>
                      )}
                    </td>
                    <td>
                      <div>{r.clinic?.clinicName || r.clinic?.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.clinic?.email}</div>
                    </td>
                    <td style={{ minWidth: '200px' }}>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.8125rem' }}
                        value={r.assignedUser?._id || ''}
                        disabled={busy}
                        onChange={(e) => saveAssignUser(r._id, e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {assigneeOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className="badge badge-info">{r.status}</span>
                    </td>
                    <td style={{ minWidth: '220px' }}>
                      <select
                        className="form-input"
                        style={{ marginBottom: '8px' }}
                        value={labStatusDraft[r._id] ?? ''}
                        onChange={(e) => setLabStatusDraft((p) => ({ ...p, [r._id]: e.target.value }))}
                      >
                        <option value="">Set status…</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="in_progress">In progress</option>
                        <option value="on_hold">On hold</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <textarea
                        className="form-textarea"
                        placeholder="Lab note (internal)"
                        rows={2}
                        value={labNoteDraft[r._id] ?? ''}
                        onChange={(e) => setLabNoteDraft((p) => ({ ...p, [r._id]: e.target.value }))}
                      />
                      <button type="button" className="btn btn-primary" style={{ marginTop: '8px' }} disabled={busy} onClick={() => applyLabStatus(r._id)}>
                        Save status &amp; note
                      </button>
                    </td>
                    <td>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,image/*,.docx"
                        style={{ fontSize: '0.75rem', maxWidth: '160px' }}
                        onChange={(e) => uploadFiles(r._id, e.target.files)}
                      />
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px', fontSize: '0.8rem' }}>
                        {(r.attachments || []).map((a) => (
                          <li key={a._id}>
                            <button type="button" className="btn btn-outline" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => downloadAtt(r._id, a)}>
                              {a.filename}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                  {expandedReqId === r._id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bg-tertiary)', verticalAlign: 'top' }}>
                        <div style={{ padding: '12px', fontSize: '0.875rem', display: 'grid', gap: '8px', maxWidth: '720px' }}>
                          <p style={{ margin: 0 }}><strong>Description</strong></p>
                          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.description || '—'}</p>
                          {(r.clinic?.billingClientNumbers?.length > 0) && (
                            <p style={{ margin: 0 }}>
                              <strong>Client #s:</strong> {r.clinic.billingClientNumbers.join(', ')}
                            </p>
                          )}
                          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Updated {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {!filteredServiceRequests.length && <p style={{ color: 'var(--text-secondary)' }}>No requests match.</p>}
      </div>
    );
  }

  if (activeTab === 'demographics') {
    return <LabDemographicsSheet clinics={clinics} />;
  }

  if (activeTab === 'audit-logs') {
    return (
      <div className="card">
        <h2 className="card-title" style={{ marginBottom: '16px' }}>Audit log</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Actions for your lab and linked clinics (imports, exports, service requests, row edits).
        </p>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Entity</th>
                <th>ID</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log._id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{new Date(log.timestamp).toLocaleString()}</td>
                  <td><span className="badge badge-info">{log.action}</span></td>
                  <td>{log.entity}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.entityId}</td>
                  <td>{log.userName}</td>
                  <td style={{ fontSize: '0.85rem', maxWidth: '280px' }}>{log.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!auditLogs.length && <p style={{ color: 'var(--text-secondary)' }}>No log entries yet.</p>}
      </div>
    );
  }

  return null;
};

export default LabExtendedFeatures;
